/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * useCaptchaConfig — fetches GET /api/public/config on mount to learn
 * whether the captcha widget should be shown/required. Called by every
 * public auth form (Login, AdminLogin, Register, ForgotPassword).
 *
 * Deliberately NOT memoised to sessionStorage — the endpoint is two
 * booleans, and flipping CAPTCHA_ENABLED on Render should take effect on
 * the next page load for everyone. Cached in-module for the current
 * render pass only.
 *
 * Fail-secure: if the config fetch itself errors (network / 5xx), we
 * default to `captchaEnabled: true`. The TurnstileGate watchdog then
 * covers the case where the widget script also can't load.
 */

import { useEffect, useState } from 'react';

const API_URL = (() => {
  const raw = process.env.REACT_APP_API_URL || 'https://skilldelivery-platform.onrender.com/api';
  return raw.endsWith('/api') ? raw : `${raw.replace(/\/$/, '')}/api`;
})();

export default function useCaptchaConfig() {
  const [state, setState] = useState({
    ready: false,
    captchaEnabled: true, // fail-secure default until we hear otherwise
    siteKey: process.env.REACT_APP_TURNSTILE_SITE_KEY || null
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/public/config`, {
          method: 'GET',
          credentials: 'omit',
          cache: 'no-store'
        });
        if (!res.ok) throw new Error(`config HTTP ${res.status}`);
        const body = await res.json();
        if (cancelled) return;
        const cfg = (body && body.config) || {};
        setState({
          ready: true,
          captchaEnabled: cfg.captchaEnabled !== false,
          // Prefer server-provided key, fall back to build-time env.
          siteKey: cfg.turnstileSiteKey || process.env.REACT_APP_TURNSTILE_SITE_KEY || null
        });
      } catch (err) {
        if (cancelled) return;
        // Fail-secure: assume captcha is required. TurnstileGate's 8s
        // watchdog gives the user a "please refresh" message if the
        // widget itself also fails.
        console.warn('[useCaptchaConfig] fetch failed — assuming captcha required:', err && err.message);
        setState((s) => ({ ...s, ready: true }));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return state;
}
